<template>
  <div class="address-card">
    <!-- Header with alias -->
    <div class="address-header">
      <div class="address-title-row">
        <i class="fa-light fa-location-dot address-icon" />
        <span class="address-alias">{{ addressAlias }}</span>
      </div>

      <!-- Actions -->
      <div class="address-actions">
        <SpmButton
          v-if="showMapButton"
          v-tooltip.top="t('dataExplorer.contactFile.fields.addresses.showMap')"
          icon="far fa-map"
          class-style="p-button-outlined p-button-sm"
          @click="openMap"
        />
        <SpmButton
          v-if="showToDetailsButton"
          v-tooltip.top="t('dataExplorer.contactFile.fields.addresses.openAddressBtn')"
          icon="fa-light fa-circle-info"
          class-style="p-button-outlined p-button-sm"
          @click="openAddressDetails"
        />
      </div>
    </div>

    <!-- Address body -->
    <div class="address-body">
      <!-- Address line 1 -->
      <div
        v-if="address.address1"
        class="address-line"
      >
        <span class="address-text">{{ address.address1 }}</span>
      </div>

      <!-- Address line 2 -->
      <div
        v-if="address.address2"
        class="address-line"
      >
        <span class="address-text secondary">{{ address.address2 }}</span>
      </div>

      <!-- City and postal code -->
      <div
        v-if="address.city || address.postcode"
        class="address-line"
      >
        <i class="fa-light fa-map-pin address-detail-icon" />
        <span class="address-text">
          <template v-if="address.postcode">{{ address.postcode }}</template>
          <template v-if="address.city && address.postcode"> - </template>
          <template v-if="address.city">{{ address.city }}</template>
        </span>
      </div>

      <!-- Country -->
      <div
        v-if="countryLabel"
        class="address-line"
      >
        <i class="fa-light fa-globe address-detail-icon" />
        <span class="address-text">{{ countryLabel }}</span>
      </div>

      <!-- Phones -->
      <div
        v-if="address.phone1 || address.phone2"
        class="address-phones"
      >
        <div
          v-if="address.phone1"
          class="phone-item"
        >
          <i class="fa-light fa-phone address-detail-icon" />
          <span class="address-text">{{ address.phone1 }}</span>
        </div>
        <div
          v-if="address.phone2"
          class="phone-item"
        >
          <i class="fa-light fa-mobile address-detail-icon" />
          <span class="address-text">{{ address.phone2 }}</span>
        </div>
      </div>
    </div>
  </div>

  <Dialog
    v-model:visible="mapVisible"
    modal
    :header="t('dataExplorer.map')"
    :style="{ width: '50rem' }"
  >
    <AddressMap
      :address="address"
      class-name="h-20rem"
    />
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, computed, PropType, ref,
} from 'vue';
import Dialog from 'primevue/dialog';
import Tooltip from 'primevue/tooltip';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import AddressMap from '@/components/data-explorer/fields/AddressMap.vue';
import { countries } from '@/types/country-language-options';
import {
  ContactAddress,
} from '@/types/data-explorer-types';
import { useStore } from '@/store';
import { useI18n } from 'vue-i18n';

interface LatLng {
  lat: number;
  lng: number;
}

export default defineComponent({
  name: 'AddressItem',

  components: {
    SpmButton,
    Dialog,
    AddressMap,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    address: {
      type: Object as PropType<ContactAddress>,
      required: true,
    },

    showToDetailsButton: {
      type: Boolean,
      required: false,
      default: true,
    },

    showMapButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const store = useStore();

    const mapVisible = ref(false);
    const coordinate = ref<LatLng>({
      lat: 0,
      lng: 0,
    });

    // Address alias (uses address1 or a default label)
    const addressAlias = computed(() => {
      if (props.address.address1) {
        return props.address.address1.length > 40
          ? `${props.address.address1.substring(0, 40)}...`
          : props.address.address1;
      }
      return t('dataExplorer.contactFile.fields.addresses.addressLabel', { id: props.address.idShopCustomerAddress });
    });

    // Country label
    const countryLabel = computed(() => {
      if (props.address.country) {
        const country = countries.find((countryItem) => countryItem.code.toLowerCase() === props.address.country?.toLowerCase());
        return country ? country.label : props.address.country;
      }
      return null;
    });

    const openAddressDetails = async () => {
      store.dispatch('dataExplorer/openAddressFile', { idShopCustomerAddress: props.address.idShopCustomerAddress });
    };

    const openMap = async () => {
      mapVisible.value = true;
    };

    return {
      t,
      addressAlias,
      countryLabel,
      mapVisible,
      coordinate,

      openAddressDetails,
      openMap,
    };
  }
})
</script>

<style lang="scss" scoped>
.address-card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.address-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.address-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.address-icon {
  color: #94c748;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.address-alias {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.address-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  opacity: 1;
}

.address-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-left: 1.625rem; // Aligned with text after icon
}

.address-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.address-detail-icon {
  color: #9ca3af;
  font-size: 0.875rem;
  width: 1rem;
  flex-shrink: 0;
}

.address-text {
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.5;

  &.secondary {
    color: #6b7280;
  }
}

.address-phones {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.25rem;
}

.phone-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

// Responsive
@media (max-width: 768px) {
  .address-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .address-title-row {
    width: 100%;
  }

  .address-actions {
    align-self: flex-end;
  }
}
</style>